import React from 'react';
import { Constants } from '../../../Constants';
import { store } from '../../app/store';
import { getOrCreateHubConnection } from './signalRHubConnection';

// Must match the MessageRelayHub method name on the backend.
const SEND_TYPING_METHOD = 'SendTypingStateAsync';

/**
 * Customer-side typing emitter (#1770). Returns handlers that `ChatInput` calls on keystroke
 * (`onType`) and on send / when leaving the chat (`stop`). While the customer types it emits a
 * leading `true` throttled to once per {@link Constants.liveChat.TYPING_THROTTLE_MS}, and a
 * `false` after {@link Constants.liveChat.TYPING_INACTIVITY_MS} of inactivity, on send, or on
 * unmount / chat switch.
 *
 * Only emits once a human agent is handling the chat (`agentPresent`) — there is no point telling
 * the AI the customer is typing, and the backend notifier would drop it anyway. Emits go straight
 * to the hub (not via the Redux action stream, which is reserved for persisted messages); any
 * failure is swallowed because a dropped typing ping is harmless.
 */
export const useTypingEmitter = (chatId: string, agentPresent: boolean) => {
    const lastTrueSentAt = React.useRef(0);
    const isActive = React.useRef(false);
    const inactivityTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    const emit = React.useCallback(
        (isTyping: boolean) => {
            const connection = getOrCreateHubConnection(store);
            if (!connection) return;
            connection
                .then((conn) => conn.invoke(SEND_TYPING_METHOD, chatId, isTyping))
                .catch(() => {
                    /* best-effort: a dropped typing ping is harmless */
                });
        },
        [chatId],
    );

    const clearInactivityTimer = React.useCallback(() => {
        if (inactivityTimer.current) {
            clearTimeout(inactivityTimer.current);
            inactivityTimer.current = null;
        }
    }, []);

    const stop = React.useCallback(() => {
        clearInactivityTimer();
        if (isActive.current) {
            isActive.current = false;
            emit(false);
        }
    }, [clearInactivityTimer, emit]);

    const onType = React.useCallback(() => {
        if (!agentPresent) return;
        const now = Date.now();
        if (!isActive.current || now - lastTrueSentAt.current >= Constants.liveChat.TYPING_THROTTLE_MS) {
            isActive.current = true;
            lastTrueSentAt.current = now;
            emit(true);
        }
        clearInactivityTimer();
        inactivityTimer.current = setTimeout(() => {
            isActive.current = false;
            emit(false);
        }, Constants.liveChat.TYPING_INACTIVITY_MS);
    }, [agentPresent, clearInactivityTimer, emit]);

    // Emit a final `false` when the chat changes or the component unmounts, so the agent's
    // indicator clears promptly instead of waiting on the receiver safety timeout. `stop` is
    // rebound whenever chatId changes, so the cleanup always targets the chat it was bound to.
    React.useEffect(() => stop, [stop]);

    return { onType, stop };
};
